import { useState, useMemo, useEffect, use } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';


import {
  Grid,
  Button, CircularProgress, Backdrop, TextField, Snackbar,
  Alert, InputLabel, FormControl, MenuItem, Select,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography
} from '@mui/material';

import { API_BASE_URL, Heading_Text_Color, Menu_Back_Color, Menu_Text_Color } from '../../constant/constant';
import { decrypt } from '../../components/cryptoUtil';
import { Controller } from 'react-hook-form';

const QueryUploader = () => {
  const [query, setQuery] = useState('');
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [editRowIndex, setEditRowIndex] = useState(null);
  const [editRowData, setEditRowData] = useState({});
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const UserDetails = (JSON.parse(decrypt(localStorage.getItem('tokenData'))));

  const token = localStorage.getItem("token");

  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [softwares, setSoftwares] = useState([]);




  const [loadingSoftwares, setLoadingSoftwares] = useState(false);



  //const [username, setUsername] = useState('');

  const [tableList, setTableList] = useState([]);
  const [fieldList, setFieldList] = useState([]);
  const [procedureList, setProcedureList] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');

  const [prefix, setPrefix] = useState(`${UserDetails.softgroup}_`); // e.g. "rib_"



  const [selectedProcedure, setSelectedProcedure] = useState('');
  //const [procedureQuery, setProcedureQuery] = useState('');



  // useEffect(() => {
  //   const storedUser = sessionStorage.getItem('username');
  //   if (storedUser) {
  //     setUsername(storedUser);
  //    // console.log('Logged-in user:', storedUser);
  //   }
  // }, []);



  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => setSnackbar({ open: false, message: '', severity: 'success' });


  const runQuery = async () => {
    //  const prefix = `${UserDetails.softgroup}_`; // e.g. "rib_"

    // ✅ Only SELECT queries allowed
    if (!query.trim().toLowerCase().startsWith("select")) {
      return showSnackbar("❌ Only SELECT queries are allowed.", "error");
    }

    // ✅ Regex to capture table name after FROM (ignores quotes/backticks)
    const fromMatch = query.match(/\bfrom\s+[`'"]?([a-zA-Z0-9_]+)[`'"]?/i);

    if (fromMatch) {
      const tableName = fromMatch[1]; // clean table name without signs

      if (!tableName.startsWith(prefix)) {
        return showSnackbar(
          `❌ Invalid table "${tableName}". Must start with "${prefix}".`,
          "error"
        );
      }
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE_URL}/run-query`, { query }, {
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${token}`, // optional, set default headers
        },
      });
      const { columns: cols, rows } = res.data;
      const dataWithRowNo = rows.map((row, i) => ({ rowNo: i + 1, ...row }));
      setColumns(["rowNo", ...cols]);
      setData(dataWithRowNo);
      setSortConfig({ key: null, direction: "asc" });
      setCurrentPage(1);
    } catch (error) {
      console.error(error);
      showSnackbar("❌ Failed to execute query.", "error");
    } finally {
      setLoading(false);
    }
  };





  const handleSort = (column) => {
    if (column === 'Row No') return;
    setLoading(true);
    setTimeout(() => {
      setSortConfig((prev) =>
        prev.key === column
          ? { key: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
          : { key: column, direction: 'asc' }
      );
      setCurrentPage(1);
      setLoading(false);
    }, 100);
  };



  const handleRowClick = (row) => {
    setSelectedRowData(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRowData(null);
  };

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    return data.filter((row) =>
      Object.values(row).some((val) =>
        val?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [data, searchQuery]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const valA = a[sortConfig.key] ?? '';
      const valB = b[sortConfig.key] ?? '';
      return (valA > valB ? 1 : valA < valB ? -1 : 0) *
        (sortConfig.direction === 'asc' ? 1 : -1);
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleExportExcel = () => {
    const headers = columns.slice(1);
    const exportData = [headers];
    sortedData.forEach(row => {
      const rowData = headers.map(h => {
        const val = row[h];
        return typeof val === 'number' && val > 99999999999 ? `'${val}` : val;
      });
      exportData.push(rowData);
    });
    const worksheet = XLSX.utils.aoa_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'QueryResults');
    XLSX.writeFile(workbook, 'query_export.xlsx');
  };

  const handleExportCSV = () => {
    const headers = columns.slice(1);
    const exportData = [headers];
    sortedData.forEach(row => {
      const rowData = headers.map(h => {
        const val = row[h];
        return typeof val === 'number' && val > 99999999999 ? `'${val}` : val;
      });
      exportData.push(rowData);
    });
    const worksheet = XLSX.utils.aoa_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Export');
    XLSX.writeFile(workbook, 'query_export.csv');
  };


  useEffect(() => {
    const fetchSoftwarelist = async () => {

      try {
        const res = await axios.get(`${API_BASE_URL}/api/softwareslist`, {
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${token}`, // optional, set default headers
          },
        });
        setSoftwares(res.data);

      } catch (err) {
        console.error(err);
        showSnackbar('❌ Failed to fetch table list.', 'error');
      }
    }
    fetchSoftwarelist()

  }, []);








  useEffect(() => {
    const fetchTables = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/tables?prefix=${prefix}`, {
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${token}`, // optional, set default headers
          },
        });
        setTableList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        showSnackbar('❌ Failed to fetch table list.', 'error');
        setTableList([]);
      }
    };

    fetchTables();
  }, [prefix]);


  useEffect(() => {


    setQuery('');
    setColumns([]);
    setData([]);
    setSortConfig({ key: null, direction: 'asc' });
    setLoading(false);
    setCurrentPage(1);
    setPageSize(10);
    setSearchQuery('');
    setEditRowIndex(null);
    setEditRowData({});
    setSelectedRowData(null);
    setOpenDialog(false);
    //showSnackbar('🧹 Cleared all uploaded data.');


    // const fetchTables = async () => {
    //  // let tablePrefix = `${UserDetails.softgroup}_`;
    //   try {
    //     const res = await axios.get(`${API_BASE_URL}/tables?prefix=${prefix}`);
    //     setTableList(res.data);
    //   } catch (err) {
    //     console.error(err);
    //     showSnackbar('❌ Failed to fetch table list.', 'error');
    //   }
    // };

    const fetchProcedures = async () => {
      // let procedurePrefix = `${UserDetails.softgroup}_`;
      try {
        const res = await axios.get(`${API_BASE_URL}/procedures?prefix=${prefix}`, {
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${token}`, // optional, set default headers
          },
        });
        setProcedureList(res.data);
      } catch (err) {
        console.error(err);
        showSnackbar('❌ Failed to fetch procedures.', 'error');
      }
    };

    //  fetchTables();
    fetchProcedures();
  }, [prefix]);

  // useEffect(() => {
  //   if (!selectedTable) {
  //     setFieldList([]);
  //     return;
  //   }

  //   const fetchFields = async () => {
  //     try {
  //       const res = await axios.get(`${API_BASE_URL}/tables/${selectedTable}/fields`);
  //       setFieldList(res.data);
  //     } catch (err) {
  //       console.error(err);
  //       showSnackbar('❌ Failed to fetch fields.', 'error');
  //     }
  //   };

  //   fetchFields();
  // }, [selectedTable]);


  useEffect(() => {
    if (!selectedTable) {
      setFieldList([]);
      return;
    }

    const fetchFields = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/tables/${selectedTable}/fields`, {
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${token}`, // optional, set default headers
          },
        });
        setFieldList(res.data);

        // Auto-generate SELECT query with backtick-wrapped fields and table name
        const fieldsString = res.data.map(field => `\`${field}\``).join(', ');
        const autoQuery = `SELECT ${fieldsString} FROM \`${selectedTable}\` WHERE 1 LIMIT 100`;
        setQuery(autoQuery);

      } catch (err) {
        console.error(err);
        showSnackbar('❌ Failed to fetch fields.', 'error');
      }
    };

    fetchFields();
  }, [selectedTable]);

  const formatValue = (value) => {
    // Check for ISO string that looks like UTC datetime
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/)) {
      try {
        const date = new Date(value);
        const options = {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: 'Asia/Kolkata',
          hour12: true,
        };
        return new Intl.DateTimeFormat('en-IN', options).format(date);
      } catch (e) {
        return value;
      }
    }
    return value;
  };




  useEffect(() => {
    selectedSoftware && setPrefix(`${selectedSoftware}_`);
    ///console.log("Selected Software:", selectedSoftware); // ✅ log here

  }, [selectedSoftware]);







  return (
    <div style={{ padding: 20 }}>
      <Backdrop open={loading} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
        <span style={{ marginLeft: 10 }}>Loading...</span>
      </Backdrop>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>





      <Grid item xs={12} container spacing={20} alignItems="center">
        <Grid item xs={6} style={{ display: 'flex', alignItems: 'left' }}>
          {/* <h2 gutterBottom style={{ color: Heading_Text_Color, textAlign: 'center' }}>🧠 Query Explorer</h2> */}
          <Typography gutterBottom style={{ color: Heading_Text_Color, fontSize: '1.25rem' }}>
            🧠 Query Explorer
          </Typography>



        </Grid>

        <Grid item xs={6} style={{ display: 'flex', alignItems: 'right' }}>
          {data.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setQuery('');
                setColumns([]);
                setData([]);
                setSortConfig({ key: null, direction: 'asc' });
                setLoading(false);
                setCurrentPage(1);
                setPageSize(10);
                setSearchQuery('');
                setEditRowIndex(null);
                setEditRowData({});
                setSelectedRowData(null);
                setOpenDialog(false);
                showSnackbar('🧹 Cleared all uploaded data.');
              }}
            >
              🔄 Refresh
            </Button>
          )}
        </Grid>
      </Grid>

      <div>


      </div>



      <div>
        <FormControl size="small" sx={{ minWidth: 250, mb: 4 }} fullWidth>
          <InputLabel>Select Software</InputLabel>
          <Select
            value={selectedSoftware}
            onChange={(e) => {
              setSelectedSoftware(e.target.value);
              // console.log("Selected Software:", e.target.value); // ✅ log here
            }}
            label="Select Software"
          >

            <MenuItem value=""><em>-- Select Software --</em></MenuItem>
            {softwares
              .map((t) => (
                <MenuItem key={t.id} value={t.softgroupcode}>
                  {t.softwarename}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>




      <Grid container spacing={2} alignItems="center">
        {/* First Control */}
        <Grid item xs={6}>







          <FormControl size="small" sx={{ minWidth: 250 }} fullWidth>
            <InputLabel>Select Table</InputLabel>
            <Select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              label="Select Table"
            >
              <MenuItem value=""><em>-- Select Table --</em></MenuItem>
              {/* {tableList
          .filter(table => ['rib_cr_data', 'rib_dr_data', 'rib_update_data'].includes(table))
          .map((table, index) => (
            <MenuItem key={index} value={table}>{table}</MenuItem>
          ))} */}


              {tableList
                // .filter(table => ['rib_cr_data', 'rib_dr_data', 'rib_update_data'].includes(table))
                .map((table, index) => (
                  <MenuItem key={index} value={table}>{table}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Second Control */}
        <Grid item xs={6}>
          <FormControl fullWidth size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Stored Procedures</InputLabel>
            <Select
              value={selectedProcedure}
              onChange={async (e) => {
                const procName = e.target.value;
                setSelectedProcedure(procName);

                try {
                  const res = await axios.get(`${API_BASE_URL}/procedures/${procName}/definition`, {
                    headers: {
                      'Content-Type': 'application/json',
                      "Authorization": `Bearer ${token}`, // optional, set default headers
                    },
                  });
                  setQuery(res.data.createQuery);
                } catch (err) {
                  console.error(err);
                  showSnackbar('❌ Failed to load procedure definition', 'error');
                }
              }}
              label="Stored Procedures"
            >
              <MenuItem value=""><em>-- Procedures --</em></MenuItem>
              {procedureList.map((proc, index) => (
                <MenuItem key={index} value={proc}>{proc}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>






      <div>
        <div>
          <FormControl fullWidth size="small">

          </FormControl>


        </div>
        <div>



          <FormControl fullWidth size="small" disabled={!procedureList.length}>

          </FormControl>
        </div>

      </div>











      <div style={{ marginBottom: 20 }}>
        <TextField
          label="Enter SELECT Query"
          multiline
          minRows={3}
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="small"
        />
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
          <Button variant="contained" color="primary" onClick={runQuery}>
            ▶ Run Query
          </Button>
        </div>
      </div>

      {data.length > 0 && (
        <>
          <p><strong>Rows Found: </strong> {filteredData.length}</p>

          <div style={{
            background: '#f5f5f5',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Page Size</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={500}>500</MenuItem>
                    <MenuItem value={1000}>1000</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="🔍 Search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </Grid>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 10 }}>
                <Button variant="outlined" onClick={handleExportCSV}>Export as CSV</Button>
                <Button variant="outlined" onClick={handleExportExcel}>Export as Excel</Button>
              </div>
            </Grid>
          </div>

          <div style={{ maxHeight: 600, overflow: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }} border="1" cellPadding="6">
              <thead>
                <tr>
                  {columns.map((col, i) => (
                    <th
                      key={i}
                      onClick={() => handleSort(col)}
                      style={{
                        background: Menu_Back_Color,

                        color: Menu_Text_Color,
                        position: 'sticky',
                        top: 0,
                        cursor: col !== 'rowNo' ? 'pointer' : 'default'
                      }}
                    >
                      {col}
                      {sortConfig.key === col && (sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽')}
                    </th>
                  ))}
                  {/* <th>Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, index) => (
                  <tr key={index} onClick={() => handleRowClick(row)} style={{ cursor: 'pointer' }}>
                    {/* {columns.map((col, i) => (
                      <td key={i}>
                        {editRowIndex === index && col !== 'Row No' ? (
                          <TextField
                            value={editRowData[col] ?? ''}
                            onChange={(e) =>
                              setEditRowData((prev) => ({ ...prev, [col]: e.target.value }))
                            }
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          row[col]
                        )}
                      </td>
                    ))} */}
                    {columns.map((col, i) => (
                      <td key={i}>
                        {editRowIndex === index && col !== 'Row No' ? (
                          <TextField
                            value={editRowData[col] ?? ''}
                            onChange={(e) =>
                              setEditRowData((prev) => ({ ...prev, [col]: e.target.value }))
                            }
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          formatValue(row[col])
                        )}
                      </td>
                    ))}

                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <Button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>⬅ Prev</Button>
            <span style={{ margin: '0 15px' }}>Page {currentPage} of {totalPages}</span>
            <Button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next ➡</Button>
          </div>
        </>
      )}

      {/* Dialog for Row Details with Copy & Print */}
      {selectedRowData && (
        <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>📋 Row Details</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} id="print-area">
              {columns.filter(col => col !== 'rowNo').map((col, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <strong>{col}:</strong> {selectedRowData[col]?.toString() ?? ''}
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                const filteredData = columns
                  .filter(col => col !== 'rowNo')
                  .map(col => `${col}: ${selectedRowData[col]}`)
                  .join('\n');
                navigator.clipboard.writeText(filteredData);
                showSnackbar("✅ Copied to clipboard");
              }}
            >
              📋 Copy
            </Button>
            <Button
              onClick={() => {
                const printContent = document.getElementById('print-area').innerHTML;
                const printWindow = window.open('', '', 'height=600,width=800');
                printWindow.document.write('<html><head><title>Row Details</title></head><body>');
                printWindow.document.write('<h3>Row Details</h3>');
                printWindow.document.write(printContent);
                printWindow.document.write('</body></html>');
                printWindow.document.close();
                printWindow.print();
              }}
            >
              🖨️ Print
            </Button>
            <Button onClick={handleCloseDialog} color="primary">Close</Button>
          </DialogActions>
        </Dialog>
      )}


    </div>
  );
};

export default QueryUploader;